import { default as setupFormValidation, validateForm } from './modules/validate-form.js';
import setupPasswordToggle from './modules/password-toggle.js';
import syncCountryField from './modules/sync-country-field.js';

import {
	getRegionUrl,
	getCookieValue,
	getQueryStringValue,
	gtmPush,
	setCookie,
	showNextStep,
	showProductError,
	toggleSpinner,
	updateEmailPlaceholders,
	resetForm,
} from './modules/common-utils.js';
import {
	setupEmailValidation,
	setupFreeEmailCheck,
} from './modules/email-field.js';
import {
	sendSignup,
} from './modules/sendSignup.js';
import {
	renderRecaptcha,
} from './modules/recaptcha.js';
import {
	emailRequest,
	tokenRequest,
	tokenVerifyRequest,
} from './modules/requests.js';
import './modules/sso.js';



const expireTime = () => {
	const date = new Date();
	date.setTime(date.getTime() + 18000000);
	return date;
}



const checkSkip = () => {
	const firstStep = document.querySelectorAll('.slf-step')[0];
	const canSkip = [
		(getQueryStringValue('state') === 'activate'),
		(getQueryStringValue('utm_medium') === 'email'),
		(getQueryStringValue('utm_source') === 'db_n'),
		(getQueryStringValue('utm_campaign') === 'platform_SLF'),
	].every((checkQueryString) => checkQueryString === true);

	if (canSkip) {
		showNextStep(firstStep);
	}
};



/**
 * display mobile warning
 */
const mobilewarning = () => {
	const warningElement = document.getElementById('mobile-warning');

	if (warningElement && /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
		warningElement.style.display = 'block';
	}
};





/**
 * form submit takeover
 * Marketo API:
 * 		https://experienceleague.adobe.com/en/docs/marketo-developer/marketo/javascriptapi/forms-api-reference
 * Form post on form submit takeover:
 * 		https://nation.marketo.com/t5/product-discussions/best-way-to-capture-lead-information-from-non-marketo-form/m-p/83435/highlight/true#M48104
 *
 */

const attachMarketoHandlers = (formElement, marketoForm) => {
	const formId = marketoForm.getId();
	const currentStep = formElement.closest('.slf-step');

	formElement.addEventListener('submit', async (event) => {
		event.preventDefault();
		const fields = new FormData(formElement);
		const marketoFields = {};
		const genericError = 'We were unable to create your account.';
		const queryStringParams = {}
		const qsContentUTM = getQueryStringValue('utm_content');
		let slfAuthenticationToken = false;
		let productSuccess = false;

		toggleSpinner('show', event.target);

		if (qsContentUTM) {
			queryStringParams['Adwords_Content__c'] = qsContentUTM;
		}

		if (formId === 5685) {
			slfAuthenticationToken = await tokenRequest(fields).catch(() => {
				showProductError('There was a problem processing your request.');
				toggleSpinner('hide', event.target);
				gtmPush('sumo_trial_error', {
					slfErrorType: 'token',
				});
			});
		}

		if (formId === 5688) {
			fields.append('NumberOfEmployees', '250');
		}

		Array.from(fields).filter((field) => field[1] !== '' && field[0] !== 'Password').forEach((field) => {
			const [key, fieldValue] = [...field];

			/**
				* start validation hack

				* ! This is a load-bearing hack. All code from this comment to end-hack line
				* ! can be removed if Marketo no longer requires the slfSignUpRegion field.
				* ! As long as Marketo still expects the slfSignUpRegion field, this hack
				* ! must remain.
				*
				* The slfSignUpRegion field is required by both step 1/signup Marketo and the PD
				* signup API (passed through step 2/activation). However, because the PD API
				* requires it and needs it, but Marketo requires it and does not use it, we
				* hardcode the value on step 1 to `North America: US` so the form passes validation.
				*
				* The additional check on whether the value passed to Marketo is one of the values
				* Marketo expects is a result of the dropdown changing in Marketo without the
				* value changing in the sign-up form back in November 2024.
				* https://sumologic.atlassian.net/browse/SUMO-253761
				*/
			const expectedMarketoValues = [...document.querySelectorAll('#mktoForm_5685 #slfSignUpRegion option')].map((opt) => {
				return opt.value
			}).filter((val) => val !== '');

			if (key === 'slfSignUpRegion' && expectedMarketoValues.includes('North America: US')) {
				marketoFields[key] = 'North America: US';
				return false;
			}
			/**
			 * ! end validation hack
			 */

			// another validation hack, i think the field name is wrong
			if (key === 'sumoServiceLicenseAgreement' && fieldValue === 'on') {
				marketoFields['Sumo_Service_License_Agreement__c'] = true;
				return false;
			}

			// Can't have two fields with the same name, but Marketo expects only
			// one field here, named State, regardless whether it is a state or a
			// province.
			if (key === 'Province') {
				marketoFields['State'] = fieldValue;
				return false;
			}

			marketoFields[key] = fieldValue;
		});

		marketoForm.addHiddenFields({
			slfAuthenticationToken,
			...marketoFields,
			...queryStringParams,
		});

		marketoForm.onSuccess((a) => {
			updateEmailPlaceholders(fields.get('Email'));
			showNextStep(currentStep, fields);

			if (formId === 5685) {
				gtmPush('sumo_trial_signup');
			}

			if (formId === 5688) {
				gtmPush('sumo_trial_activate', {
					signupMethod: (fields.oauth || 'email'),
				});
			}
			return false;
		});

		marketoForm.onValidate((isValid) => {
			const forms = {
				5685: 'signup',
				5688: 'activate',
			}
			const formType = forms[formId];

			if (!isValid) {
				gtmPush('sumo_trial_error', {
					slfErrorType: 'marketo',
					slfErrorDetails: `Marketo validation failed on ${formType}`,
				});
			}
		})

		if (formId === 5688) {
			productSuccess = await sendSignup(fields).then((response) => {
				return response;
			}).then(async (response) => {
				const {
					apiSessionId,
					sumoServiceId,
				} = response;
				let apiSuccess = false;

				if (apiSessionId) {
					apiSuccess = true;
				} else {
					apiSuccess = false;
					showProductError(genericError);
					gtmPush('sumo_trial_error', {
						slfErrorType: 'api',
						slfErrorDetails: 'api signup error',
					});
				}

				return apiSuccess;
			}).catch((err) => {
				toggleSpinner('hide', event.target);
				gtmPush('sumo_trial_error', {
					slfErrorType: 'api',
					slfErrorDetails: err.message,
				});
			});
		}

		if ((formId === 5685 && slfAuthenticationToken) || (formId === 5688 && productSuccess === true)) {
			marketoForm.submit();
		}
	});
}



const setupMarketoForms = () => {
	const marketoFormElements = document.querySelectorAll('.mktoForm');

	marketoFormElements.forEach((marketoFormElement) => {
		const formId = marketoFormElement.id.split('_')[1];
		const slfForm = document.querySelector(`[data-form-id="${formId}"]`);

		MktoForms2.loadForm('https://info.sumologic.com/index.php/form/getForm', '793-NKB-950', formId, function (loadedMarketoForm) {
			attachMarketoHandlers(slfForm, loadedMarketoForm);

			if (formId === '5688') {
				syncCountryField();
			}

			loadedMarketoForm.onSuccess(() => {
				return false;
			});
		});
	});
}

const appendHiddenInput = (name, value, form) => {
	const p = document.createElement('input');
	p.name = name;
	p.value = value;
	p.type = 'hidden';
	form.appendChild(p);
}



const checkForToken = () => {
	if (getQueryStringValue('token')) {
		resetForm('slf-pending');
		window.sessionStorage.setItem('isPending', true);
	}
}



const removePasswordField = () => {
	const el = document.querySelector('#slf-activate .input-wrap:has(#password)');
	el.remove();
}



const addSSOSignal = () => {
	const form = document.querySelector('#slf-activate form');
	const field = document.createElement('input');
	field.type = 'hidden';
	field.name = 'oauth';
	field.value = 'oauth';
	form.appendChild(field);
}



const showSSOSLA = () => {
	const ssoSla = document.getElementById('wrap-agree-sso');
	const ssoSlaCheckbox = ssoSla.querySelector('input');
	ssoSla.removeAttribute('inert');
	ssoSlaCheckbox.setAttribute('required', true);
}



window.addEventListener('load', async (event) => {
	const token = getQueryStringValue('token');
	const ssoError = getQueryStringValue('ssoError');

	renderRecaptcha();

	if (window.sessionStorage.getItem('isPending') === 'true' && token) {
		return tokenVerifyRequest(token).then(async (fields) => {
			const activateStep = document.querySelector('#slf-activate');
			const activateForm = activateStep.querySelector('form');
			const isEmailStillValid = await emailRequest({
				email: fields.Email,
			}).catch((e) => {
				return false
			});

			if (isEmailStillValid !== false) {
				updateEmailPlaceholders(fields.Email);
				appendHiddenInput('token', token, activateForm);
				appendHiddenInput('Email', fields.Email, activateForm);

				if (fields.oauthSignup === true) {
					removePasswordField();
					addSSOSignal();
					showSSOSLA();
				}

				Object.keys(fields)
					.map((field) => activateForm.querySelector(`input[name=${field}]`))
					.filter(p => p !== null)
					.forEach((input) => {
						input.value = fields[input.name];
					});

				gtmPush('sumo_trial_token_verify');
				resetForm('slf-activate');
				validateForm(activateForm)
			} else {
				resetForm('slf-signup');
				gtmPush('sumo_trial_error', {
					slfErrorType: 'activation',
					slfErrorDetails: 'account existed at activation',
				});
			}


		}).catch((e) => {
			resetForm('slf-signup');
			validateForm(document.querySelector('#slf-signup form'));
			showProductError('Your trial request has expired. Please sign up again.');
			gtmPush('sumo_trial_error', {
				slfErrorType: 'token',
				slfErrorDetails: 'expired trial request',
			});
		});
	} else if (ssoError) {

		if (ssoError === 'sso4') {
			showProductError('An account already exists with that email. Please <a href="https://service.sumologic.com">sign in</a>.');
		} else {
			showProductError('There was a problem processing your sign-up. Please try again or choose sign-up with email.');
		}

		gtmPush('sumo_trial_error', {
			slfErrorType: 'sso',
			slfErrorDetails: ssoError,
		});
	} else {
		/**
		 * This catches cases when the signup page is valid
		 * but gets refreshed for some reason.
		 */
		validateForm(document.querySelector('#slf-signup form'));
	}
});

checkSkip();
setupMarketoForms();
mobilewarning();
setupFreeEmailCheck();
setupEmailValidation();
checkForToken();
setupFormValidation();
setupPasswordToggle();
